import type { DateTimeValue } from '../../../types/form/form-fields/field-values';
import type { ConstraintFunction } from '../../../types/form/form-section/config';

export enum DateTimeUnit {
	HOURS = 'hours',
	DAYS = 'days',
	WEEKS = 'weeks',
	MONTHS = 'months',
}

// Converts DateTime to ISO string for comparison (YYYY-MM-DDTHH:mm:ss format)
// Returns empty string if either date or time is missing
const getDateTimeString = (value: DateTimeValue): string => {
	if (!value.date || !value.localTime) {
		return '';
	}
	return `${value.date}T${value.localTime}`;
};

export const dateTimeValueEqualToCheck: ConstraintFunction<DateTimeValue> = (
	value: DateTimeValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return false;
	}
	const valueStr = getDateTimeString(value);
	return valueStr !== '' && valueStr === constraint[0];
};

export const dateTimeValueDoesNotEqualToCheck: ConstraintFunction<DateTimeValue> = (
	value: DateTimeValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return true;
	}
	const valueStr = getDateTimeString(value);
	return valueStr !== constraint[0];
};

export const dateTimeValueGreaterThanCheck: ConstraintFunction<DateTimeValue> = (
	value: DateTimeValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return false;
	}
	const valueStr = getDateTimeString(value);
	return valueStr !== '' && valueStr > constraint[0];
};

export const dateTimeValueGreaterThanOrEqualToCheck: ConstraintFunction<DateTimeValue> = (
	value: DateTimeValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return false;
	}
	const valueStr = getDateTimeString(value);
	return valueStr !== '' && valueStr >= constraint[0];
};

export const dateTimeValueLessThanCheck: ConstraintFunction<DateTimeValue> = (
	value: DateTimeValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return false;
	}
	const valueStr = getDateTimeString(value);
	return valueStr !== '' && valueStr < constraint[0];
};

export const dateTimeValueLessThanOrEqualToCheck: ConstraintFunction<DateTimeValue> = (
	value: DateTimeValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return false;
	}
	const valueStr = getDateTimeString(value);
	return valueStr !== '' && valueStr <= constraint[0];
};

// BETWEEN: Inclusive of minimum and maximum
export const dateTimeValueBetweenCheck: ConstraintFunction<DateTimeValue> = (
	value: DateTimeValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return false;
	}
	const valueStr = getDateTimeString(value);
	if (valueStr === '') {
		return false;
	}
	const min = constraint[0];
	const max = constraint[1];
	return valueStr >= min && valueStr <= max;
};

export const dateTimeValueEmptyCheck: ConstraintFunction<DateTimeValue> = (
	value: DateTimeValue | undefined,
) => value === undefined || (!value.date && !value.localTime);

export const dateTimeValueNotEmptyCheck: ConstraintFunction<DateTimeValue> = (
	value: DateTimeValue | undefined,
) => value !== undefined && (!!value.date || !!value.localTime);
